import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, 
  TableBody, TableContainer, Paper, Chip, Alert, IconButton,
  Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import SkeletonBlock from '../ui/SkeletonBlock';
import EmptyState from '../ui/EmptyState';

const statusColors = {
  PendingDM: 'warning',
  PendingBoard: 'warning', 
  Approved: 'info',
  Ordered: 'info',
  Fulfilled: 'success',
  Rejected: 'error'
};

const statusLabels = {
  PendingDM: 'Pending DM Approval',
  PendingBoard: 'Pending Board Approval',
  Approved: 'Approved - Awaiting Order',
  Ordered: 'Ordered',
  Fulfilled: 'Fulfilled',
  Rejected: 'Rejected'
};

export default function MyEquipmentRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await equipmentService.getMyRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <SkeletonBlock lines={5} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!requests.length) {
    return (
      <EmptyState 
        icon={<AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />}
        title="No Equipment Requests"
        description="You haven't submitted any equipment requests yet. Use the 'New Request' tab to request equipment."
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          My Equipment Requests ({requests.length})
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadData} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Item Type</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Est. Price</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((req) => (
              <TableRow key={req.id}>
                <TableCell>
                  {req.requestDate ? new Date(req.requestDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>{req.categoryName}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {req.subcategoryName}
                  </Typography>
                </TableCell>
                <TableCell>{req.quantity}</TableCell>
                <TableCell>
                  {req.estimatedPrice ? `${req.estimatedPrice.toFixed(2)} лв.` : '-'}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={statusLabels[req.status] || req.status} 
                    color={statusColors[req.status] || 'default'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

